import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { get } from 'lodash'
import { notification, modal } from 'redux/modules/app'
import { configuration as configurationApi } from 'redux/modules/api_v2'
import mapSchema from 'models/api_v2/configurations/mapSchema'
import { CONFIGURATION_KEYS } from 'constants/enums'

/**
 * Organization used for public reads when no organization is known yet.
 * For example: password policy when registering
 */
const PUBLIC_ORGANIZATION_ID = 1

function decorator(config = {}) {
  return ComposedComponent => {
    @connect(
      ({ apiV2, auth }) => ({
        configuration: apiV2.configuration,
        activeOrganizationId: get(auth, 'organization.id'),
        isAuthenticated: Boolean(get(auth, 'user')),
      }),
      { ...configurationApi, notification, modal },
    )
    class Configuration extends Component {
      static propTypes = {
        getConfigurationValue: PropTypes.func.isRequired,
        getConfigurationValues: PropTypes.func.isRequired,
        configuration: PropTypes.object.isRequired,
        notification: PropTypes.func.isRequired,
        activeOrganizationId: PropTypes.number,
        isAuthenticated: PropTypes.bool,
      }

      componentDidMount() {
        config.getValue && this.handleGetConfigurationValue(config.getValue)
        config.getValues && this.handleGetConfigurationValues(config.getValues)
      }

      resolveOrganizationId = (organizationId, removeToken) => {
        if (organizationId) {
          return organizationId
        }

        if (this.props.activeOrganizationId) {
          return this.props.activeOrganizationId
        }

        // No identity at all
        if (removeToken || !this.props.isAuthenticated) {
          return PUBLIC_ORGANIZATION_ID
        }

        return undefined
      }

      handleGetConfigurationValue = (value, organizationId, removeToken) => {
        return this.props
          .getConfigurationValue(value, {
            organizationId: this.resolveOrganizationId(organizationId, removeToken),
            removeToken
          })
          .then(response => ({
            ...response,
            body: mapSchema(get(response, 'body.dtoList[0]', {})),
          }))
      }

      handleGetConfigurationValues = (values, organizationId, removeToken) => {
        return this.props
          .getConfigurationValues(values, {
            organizationId: this.resolveOrganizationId(organizationId, removeToken),
            removeToken
          })
          .then(response => ({
            ...response,
            values: this.mapDtoListToValues(get(response, 'body.dtoList', [])),
          }))
          .catch(this.handleError)
      }

      // v2 response (ConfigurationDto list) -> { KEY: legacy value }
      mapDtoListToValues = (dtoList = []) =>
        dtoList.reduce((acc, dto) => {
          const keyName = get(dto, 'key.name.type')

          return keyName ? { ...acc, [keyName]: mapSchema(dto) } : acc
        }, {})

      handleGetRegistrationFieldsCustomization = (organizationId, extraKeys = []) => {
        return this.handleGetConfigurationValues([
            ...extraKeys,
            CONFIGURATION_KEYS.participantRegistrationEmailAddress2.id,
            CONFIGURATION_KEYS.participantRegistrationPhoneType.id,
            CONFIGURATION_KEYS.participantRegistrationSecondaryPhone.id,
            CONFIGURATION_KEYS.participantRegistrationSecPhoneType.id,
            CONFIGURATION_KEYS.participantRegistrationTitle.id,
            CONFIGURATION_KEYS.participantRegistrationMiddleName.id,
            CONFIGURATION_KEYS.participantRegistrationNationality.id,
            CONFIGURATION_KEYS.participantRegistrationEmplStatus.id,
            CONFIGURATION_KEYS.participantRegistrationAddressLine2.id,
            CONFIGURATION_KEYS.participantRegistrationAddressLine1.id,
            CONFIGURATION_KEYS.participantRegistrationPhoneNumber.id,
            CONFIGURATION_KEYS.participantRegistrationAddressCity.id,
            CONFIGURATION_KEYS.participantRegistrationAddressZipCode.id,
            CONFIGURATION_KEYS.participantRegistrationAddressRegion.id,
            CONFIGURATION_KEYS.participantRegistrationAddressCountry.id,
            CONFIGURATION_KEYS.participantAge.id,
            CONFIGURATION_KEYS.postalAddress.id,
          ],
          organizationId,
        )
      }

      handleGetLeaderRegistrationFieldsCustomization = organizationId => {
        return this.handleGetConfigurationValues([
            CONFIGURATION_KEYS.organizationContactEmailAddress2.id,
            CONFIGURATION_KEYS.organizationContactPhoneNumber.id,
            CONFIGURATION_KEYS.participantRegistrationSecondaryPhone.id,
            CONFIGURATION_KEYS.organizationContactMiddleName.id,
            CONFIGURATION_KEYS.organizationContactGender.id,
            CONFIGURATION_KEYS.organizationContactDateOfBirth.id,
            CONFIGURATION_KEYS.organizationContactAddressLine1.id,
            CONFIGURATION_KEYS.organizationContactAddressLine2.id,
            CONFIGURATION_KEYS.organizationContactAddressCity.id,
            CONFIGURATION_KEYS.organizationContactAddressZipCode.id,
            CONFIGURATION_KEYS.organizationContactAddressRegion.id,
            CONFIGURATION_KEYS.organizationContactAddressCountry.id,
            CONFIGURATION_KEYS.organizationContactRole.id,
            CONFIGURATION_KEYS.organizationContactTitle.id,
          ],
          organizationId,
        )
      }

      handleGetOrgContactRegistrationFieldsCustomization = organizationId => {
        return this.handleGetConfigurationValues([
            CONFIGURATION_KEYS.organizationContactEmailAddress2.id,
            CONFIGURATION_KEYS.organizationContactPhoneNumber.id,
            CONFIGURATION_KEYS.organizationContactMiddleName.id,
            CONFIGURATION_KEYS.organizationContactGender.id,
            CONFIGURATION_KEYS.organizationContactDateOfBirth.id,
            CONFIGURATION_KEYS.organizationContactAddressLine1.id,
            CONFIGURATION_KEYS.organizationContactAddressLine2.id,
            CONFIGURATION_KEYS.organizationContactAddressCity.id,
            CONFIGURATION_KEYS.organizationContactAddressZipCode.id,
            CONFIGURATION_KEYS.organizationContactAddressRegion.id,
            CONFIGURATION_KEYS.organizationContactAddressCountry.id,
          ],
          organizationId,
        )
      }

      handleGetAwardUnitRegistrationFieldsCustomization = (organizationId, extraKeys = []) => {
        return this.handleGetConfigurationValues([
            ...extraKeys,
            CONFIGURATION_KEYS.organizationContactPhoneNumber.id,
            CONFIGURATION_KEYS.organizationContactMiddleName.id,
            CONFIGURATION_KEYS.organizationContactGender.id,
            CONFIGURATION_KEYS.organizationContactAddressLine1.id,
            CONFIGURATION_KEYS.organizationContactAddressLine2.id,
            CONFIGURATION_KEYS.organizationContactAddressCity.id,
            CONFIGURATION_KEYS.organizationContactAddressZipCode.id,
            CONFIGURATION_KEYS.organizationContactAddressRegion.id,
            CONFIGURATION_KEYS.organizationContactAddressCountry.id,
          ],
          organizationId,
        )
      }

      isConfigurationTrue = configKey => {
        const values = this.props.configuration && this.props.configuration.values

        const value = values && values[configKey]

        return value ? Boolean(value.booleanValue) : false
      }

      handleError = ({ body } = {}) => {
        console.debug('error', get(body, 'userMessage'))
        this.props.notification(get(body, 'userMessage'), 'error')
      }

      render() {
        return (
          <ComposedComponent
            {...this.props}
            onGetConfigurationValue={this.handleGetConfigurationValue}
            onGetConfigurationValues={this.handleGetConfigurationValues}
            // Legacy action names kept for components that call them directly.
            getConfigurationKeyValue={this.handleGetConfigurationValue}
            getConfigurationKeysValues={this.handleGetConfigurationValues}
            handleGetRegistrationFieldsCustomization={this.handleGetRegistrationFieldsCustomization}
            handleGetLeaderRegistrationFieldsCustomization={this.handleGetLeaderRegistrationFieldsCustomization}
            handleGetOrgContactRegistrationFieldsCustomization={this.handleGetOrgContactRegistrationFieldsCustomization}
            handleGetAwardUnitRegistrationFieldsCustomization={this.handleGetAwardUnitRegistrationFieldsCustomization}
            isConfigurationTrue={this.isConfigurationTrue}
          />
        )
      }
    }

    return Configuration
  }
}

export default decorator
